// enums
import { EmployeeType } from 'components/enums';
import { RosterPlanModeEnum } from './RosterPlan.enums';

// types
import type {
  RosterPlanFilterDescriptor,
  RosterPlanModeMeta,
  RosterPlanSwitchableMode
} from './RosterPlan.types';

/**
 * The filter groups of the roster screen, in the order they are rendered, and the single source
 * for both the rendering order and `FILTER_KEYS`.
 *
 * Order is load bearing twice over: `RosterPlanFilters` renders the array as it comes, and the
 * applied selection is built by walking `FILTER_KEYS`. Those keys are derived from this list below,
 * so the two cannot drift — adding a group here reaches `onApplyFilters` on its own, and removing
 * one cannot leave an orphaned key behind.
 *
 * A group either derives its options from a data source (`source` + the field pair to read) or
 * carries a fixed set of ids; `useRosterPlanFilterOptions` turns both into option lists.
 */
export const ROSTER_PLAN_FILTER_DESCRIPTORS = [
  {
    key: 'costCenter',
    source: 'employees',
    labelKey: 'customerName',
    idKey: 'customerId'
  },
  {
    key: 'function',
    source: 'employees',
    labelKey: 'benefits.functionName',
    idKey: 'benefits.functionId'
  },
  {
    key: 'employees',
    source: 'employees',
    labelKey: 'nameInformal',
    idKey: 'id'
  },
  {
    key: 'planned',
    options: [
      { id: 'planned', labelKey: 'planned' },
      { id: 'notPlanned', labelKey: 'notPlanned' }
    ]
  },
  {
    key: 'types',
    options: [
      { id: EmployeeType.OWN, labelKey: `employeeType.${EmployeeType.OWN}` },
      {
        id: EmployeeType.FLEX,
        labelKey: `employeeType.${EmployeeType.FLEX}`
      },
      {
        id: EmployeeType.BORROWING,
        labelKey: `employeeType.${EmployeeType.BORROWING}`
      },
      { id: EmployeeType.PREF, labelKey: `employeeType.${EmployeeType.PREF}` }
    ]
  },
  {
    key: 'qualifications',
    source: 'qualifications',
    labelKey: 'name',
    idKey: 'qualificationId'
  },
  {
    key: 'dutyCodes',
    source: 'duties',
    labelKey: 'displayDutyCode',
    idKey: 'dutyId'
  },
  {
    key: 'dutyHourTypes',
    source: 'dutyHourTypes',
    labelKey: 'code',
    idKey: 'id'
  },
  {
    key: 'absences',
    options: [
      { id: 'absences', labelKey: 'absencesPeriod' },
      { id: 'leaveRequests', labelKey: 'leaveRequests' }
    ]
  },
  {
    key: 'costCenterOpenRequests',
    label: 'costCenter',
    section: 'openRequests',
    source: 'openRequests',
    labelKey: 'costCenterName',
    idKey: 'costCenterId'
  },
  {
    key: 'functionOpenRequests',
    label: 'function',
    section: 'openRequests',
    source: 'openRequests',
    labelKey: 'functionName',
    idKey: 'functionId'
  },
  {
    key: 'qualificationsOpenRequests',
    label: 'qualifications',
    section: 'openRequests',
    source: 'qualifications',
    labelKey: 'name',
    idKey: 'qualificationId'
  },
  {
    key: 'dutyCodesOpenRequests',
    label: 'dutyCodes',
    section: 'openRequests',
    source: 'openRequests',
    labelKey: 'displayDutyCode',
    idKey: 'dutyId'
  },
  {
    key: 'dutyHourTypesOpenRequests',
    label: 'dutyHourTypes',
    section: 'openRequests',
    source: 'openRequestDutyHourTypes',
    labelKey: 'code',
    idKey: 'id'
  },
  {
    key: 'statusOpenRequests',
    label: 'status',
    section: 'openRequests',
    options: [
      { id: 'open', labelKey: 'open' },
      { id: 'sendToFlex', labelKey: 'sendToFlex' },
      { id: 'openForSelfMatch', labelKey: 'openForSelfMatch' },
      { id: 'subscribeSelfMatch', labelKey: 'subscribeSelfMatch' }
    ]
  }
] as const satisfies readonly RosterPlanFilterDescriptor[];

/**
 * Derived, never hand-maintained. `RosterPlanFilters` renders the descriptors in array order and
 * `onApplyFilters` walks these keys, so the two must agree on membership *and* order — deriving one
 * from the other makes that structural instead of a convention a spec has to police.
 */
export type FilterKey = (typeof ROSTER_PLAN_FILTER_DESCRIPTORS)[number]['key'];

export const FILTER_KEYS: readonly FilterKey[] =
  ROSTER_PLAN_FILTER_DESCRIPTORS.map((descriptor) => descriptor.key);

export const EMPTY_ROSTER_PLAN_FILTERS = Object.fromEntries(
  FILTER_KEYS.map((key) => [key, null])
) as Record<FilterKey, null>;

/**
 * Height in pixels of one duty within a grid row. A row is this tall times the busiest day it
 * shows, so a day with three duties makes the whole row three duties high.
 */
export const ROW_HEIGHT_PER_DUTY = 52;

// shared between the employee grid and the open-requests grid: both pin the
// same balance columns to the right and skip the dashed border on them
export const BALANCE_COLUMN_FIELDS = [
  'commitmentHours',
  'balanceWeek',
  'balance',
  'cumulative',
  'worked',
  'leave',
  'year',
  'plb',
  'plbYear'
] as const;

/**
 * Icon and label per mode of the mode switch. The toolbar action shows the icon of the active mode,
 * so the planner can read the mode off the toolbar without opening the menu.
 */
export const ROSTER_PLAN_MODE_META: Record<
  RosterPlanSwitchableMode,
  RosterPlanModeMeta
> = {
  [RosterPlanModeEnum.View]: { icon: 'book-open', labelKey: 'view' },
  [RosterPlanModeEnum.Plan]: { icon: 'pen', labelKey: 'plan' },
  [RosterPlanModeEnum.ChangesAfterPublication]: {
    icon: 'clock-rotate-left',
    labelKey: 'changesAfterPublication'
  }
};

/**
 * The react-query keys of the two grid queries. Shared, because more than their own hook targets
 * them: the live updates and a finished automatic rostering run both invalidate by these keys, and
 * a renamed key would otherwise silently stop refreshing the grids.
 */
export const EMPLOYEES_QUERY_KEY = 'employeesBasedOnCostCenters';
export const OPEN_REQUESTS_QUERY_KEY = 'rosterPlanOpenRequests';

/**
 * How live updates coalesce eventbus messages into one refetch
 * (`utils/createCoalescedInvalidator.ts`). An hours recalculation publishes a message per work
 * agreement it recalculates on every month address of that year, so a bulk action elsewhere arrives
 * as a burst; the refetch waits for a quiet second — but never longer than three, so once its message
 * is here a change after a quiet spell starts a refetch within three seconds. How long the message takes
 * is the backend's: a duty change goes through the shared hours recalculation queue first.
 *
 * A long stream — a base plan rollout recalculating hundreds of work agreements — would still refetch
 * a grid back to back, and where a selected cost centre has `dtr.calculation.enabled` every employees
 * GET also queues a DTR run on the data service's one DTR thread, which all tenants share. So a grid is refetched at most once per `LIVE_UPDATE_MIN_GAP_MS`;
 * during such a stream it updates every ten seconds instead of continuously.
 */
export const LIVE_UPDATE_QUIET_MS = 1_000;
export const LIVE_UPDATE_MAX_WAIT_MS = 3_000;
export const LIVE_UPDATE_MIN_GAP_MS = 10_000;

/**
 * The gap for a grid whose last load failed, counted from that failure. Live updates keep retrying
 * it, so a grid that failed during a deploy recovers without the planner navigating, but slowly
 * enough that an outage costs one failing month GET — and one error snackbar — a minute, not one
 * every ten seconds.
 */
export const LIVE_UPDATE_ERROR_GAP_MS = 60_000;

/**
 * A restart of the service behind the eventbus drops every socket at once, and every client retries
 * on the same schedule, so they all reconnect together. The catch-up refetch after a reconnect is
 * spread over this window, so every open roster of every tenant does not hit the restarted service
 * in the same second.
 */
export const LIVE_UPDATE_RECONNECT_SPREAD_MS = 15_000;
